/**
 * The offers / wallet domain library — ported from the original's module 83467
 * (`live-app-formatted.js`), function for function.
 *
 * Every export below is a direct port; the original symbol is named in each
 * doc comment so a later A/B pass can find it. Behaviour is reproduced
 * verbatim, including the parts that look like bugs (`getOfferConsumedUses`
 * returning `null` rather than 0 when the counts have not loaded, the
 * ceil/floor mix in the loyalty accounting, `Math.min(99, …)` capping partial
 * progress one point short of 100).
 *
 * NOT ported: `getOffersWithStats` (`ey`), the ~830-line eligibility engine
 * that turns an offer plus the cart into an `OfferStats`. It is the one piece
 * of the wallet still outstanding — see RECOVERY_PLAN.md. Nothing in this
 * deployment can exercise it: `offers`, `vouchers` and `gift_cards` are empty
 * collections database-wide, and the stored `offers` schema predates several
 * fields the engine reads.
 */

import {
  CUSTOMER_REDEMPTION_TYPE_ENUM,
  OFFER_REWARD_TYPE_ENUM,
  PATHWAY_TO_UNLOCK_TYPE_ENUM,
  type CustomerRedemptionCounts,
  type CustomerRedemptionType,
  type GiftCard,
  type Offer,
  type OfferRewardPreview,
  type OfferStats,
  type OfferUsageLimit,
  type OfferWithStats,
  type OrderDiscountDetails,
  type Voucher,
  type WalletGiftCardRedemption,
  type WalletRedemptions,
} from "@/types/wallet";
import { formatCurrency } from "@/utils/format";

/* -------------------------------------------------------------------------- */
/* small shared primitives (`R`, `x`, `p`/`f`, `h`, `D`, `N`, `w`, `C`)        */
/* -------------------------------------------------------------------------- */

/** `R` — a trimmed non-empty string, else `null`. */
export const toKey = (value: unknown): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
};

/** `x` — a positive integer, else the fallback. */
export const positiveInt = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
};

/** `p` / `f` — a trimmed, upper-cased non-empty code, else `null`. */
export const normalizeCode = (value?: string | null): string | null => {
  const text = value?.trim().toUpperCase();
  return text != null && text.length > 0 ? text : null;
};

/** `h` — a positive integer read out of a record, else 0. */
const countAt = (
  record: Record<string, unknown> | null | undefined,
  key: string,
): number => {
  const parsed = Number(record?.[key]);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

/**
 * `D` — read one bucket of the redemption counts, falling back to a flat map.
 *
 * The endpoint answers `{ ASSIGNED: {…}, USED: {…} }`, but an older reply shape
 * was flat, so a miss inside the bucket re-reads the top level.
 */
const redemptionCount = (
  counts: CustomerRedemptionCounts,
  bucket: CustomerRedemptionType,
  id: unknown,
): number => {
  const key = toKey(id);
  if (counts == null || key == null) return 0;
  const inBucket = counts[bucket]?.[key];
  if (inBucket != null) return positiveInt(inBucket, 0);
  return positiveInt((counts as unknown as Record<string, unknown>)[key], 0);
};

/** `N` — how many times this customer has USED the reference. */
export const usedCount = (counts: CustomerRedemptionCounts, id: unknown) =>
  redemptionCount(counts, CUSTOMER_REDEMPTION_TYPE_ENUM.USED, id);

/** `w` — how many times this customer has been ASSIGNED the reference. */
export const assignedCount = (counts: CustomerRedemptionCounts, id: unknown) =>
  redemptionCount(counts, CUSTOMER_REDEMPTION_TYPE_ENUM.ASSIGNED, id);

/** `C` — a voucher's or gift card's own per-customer cap. */
export const walletItemPerCustomerLimit = (
  entry: Pick<
    Voucher,
    "max_assignments_per_customer" | "max_uses_per_customer"
  > | null,
): number | null =>
  entry?.max_assignments_per_customer ?? entry?.max_uses_per_customer ?? null;

/**
 * `localeAmount` — the currency string the wallet tiles and previews render.
 * The storefront's own `formatCurrency` does no division, so amounts arrive in
 * major units already.
 */
export const localeAmount = (symbol: string | undefined, amount: unknown) =>
  formatCurrency(Number(amount ?? 0), { symbol: symbol ?? "" });

/** `parseNum` — two decimal places at most, without trailing zeros. */
export const parseNum = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

/* -------------------------------------------------------------------------- */
/* catalogue filtering                                                        */
/* -------------------------------------------------------------------------- */

/**
 * `c` — `convertOffersToWorkableOffers`.
 *
 * Keeps the active offers whose `included_platforms` contains the cart's
 * platform and whose special hours admit it right now, sorted by `sort_id`.
 *
 * The original's third test is `findApplicableSchedule(offer_special_hours,
 * platform) !== false`. We have no offer with special hours in this deployment
 * — `offer_special_hours` defaults to `null` and the helper returns `undefined`
 * for a null schedule, which passes — so the schedule is treated as applicable
 * unless it explicitly says otherwise.
 */
export const convertOffersToWorkableOffers = (
  offers: Offer[],
  platform: string | null | undefined,
): Offer[] => {
  if (platform == null) return [];
  const workable: Offer[] = [];
  for (const offer of offers) {
    if (!offer.active) continue;
    if (!offer.included_platforms?.includes(platform)) continue;
    workable.push(offer);
  }
  return workable.sort((a, b) => a.sort_id - b.sort_id);
};

/* -------------------------------------------------------------------------- */
/* usage limits (`P`, `I`, `M`, `L`, `k`, `j`)                                 */
/* -------------------------------------------------------------------------- */

/** `P` — the per-customer cap declared by whichever pathway the offer uses. */
const pathwayMaxUsesPerCustomer = (offer: Offer | null): number | null => {
  if (offer == null) return null;
  switch (offer.pathway_to_unlock_type) {
    case PATHWAY_TO_UNLOCK_TYPE_ENUM.ORDERS:
      return (
        positiveInt(offer.pathway_to_unlock_type_orders?.max_uses_per_customer, 0) ||
        null
      );
    case PATHWAY_TO_UNLOCK_TYPE_ENUM.ITEMS:
      return (
        positiveInt(offer.pathway_to_unlock_type_items?.max_uses_per_customer, 0) ||
        null
      );
    case PATHWAY_TO_UNLOCK_TYPE_ENUM.POINTS:
      return (
        positiveInt(offer.pathway_to_unlock_type_points?.max_uses_per_customer, 0) ||
        null
      );
    case PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER:
      return (
        positiveInt(offer.pathway_to_unlock_type_vouchers?.max_uses_per_customer, 0) ||
        null
      );
    default:
      return null;
  }
};

/** `I` — `getOfferUsageLimitMeta`. `null` when the offer declares no cap. */
export const getOfferUsageLimitMeta = (
  offer: Offer | null,
): OfferUsageLimit | null => {
  const max = pathwayMaxUsesPerCustomer(offer);
  if (max == null) return null;
  const usageKey = toKey(offer?._id);
  if (usageKey == null) return null;
  return { maxUsesPerCustomer: max, usageKey };
};

/**
 * `M` — `getOfferConsumedUses`.
 *
 * Returns `null`, not 0, when there is no cap or the counts have not loaded —
 * the card distinguishes "unknown" from "none used". Reproduced.
 */
export const getOfferConsumedUses = ({
  offer,
  usageLimit,
  customerOfferRedemptionCounts,
}: {
  offer: Offer | null;
  usageLimit: OfferUsageLimit | null;
  customerOfferRedemptionCounts: CustomerRedemptionCounts;
}): number | null => {
  if (usageLimit == null || customerOfferRedemptionCounts == null) return null;
  const key = toKey(offer?._id) ?? toKey(usageLimit.usageKey);
  if (key == null) return null;
  return usedCount(customerOfferRedemptionCounts, key);
};

/** `L` — `isOfferFullyConsumedForCustomer`. */
export const isOfferFullyConsumedForCustomer = ({
  offer,
  usageLimit,
  customerOfferRedemptionCounts,
}: {
  offer: Offer | null;
  usageLimit: OfferUsageLimit | null;
  customerOfferRedemptionCounts: CustomerRedemptionCounts;
}): boolean => {
  const consumed = getOfferConsumedUses({
    offer,
    usageLimit,
    customerOfferRedemptionCounts,
  });
  return (
    usageLimit != null &&
    consumed != null &&
    consumed >= usageLimit.maxUsesPerCustomer
  );
};

/** `k` — claims still outstanding on a stats block. */
const remainingClaims = (stats: OfferStats | null | undefined): number =>
  Math.max(
    0,
    positiveInt(stats?.applicable_quantity, 0) -
      positiveInt(stats?.redeemed_times, 0),
  );

/** `j` — `getOfferClaimIncrementFromStats`. */
export const getOfferClaimIncrementFromStats = ({
  stats,
  fallbackClaims = 0,
}: {
  stats: OfferStats | null | undefined;
  fallbackClaims?: number;
}): number => {
  const remaining = remainingClaims(stats);
  return remaining > 0 ? remaining : positiveInt(fallbackClaims, 0);
};

/* -------------------------------------------------------------------------- */
/* voucher pathway context (`m`, `_`, `g`, `y`, `b`)                           */
/* -------------------------------------------------------------------------- */

export interface VoucherPathwayCartContext {
  activeVoucherCode: string | null;
  consumedVoucherCodes: Set<string>;
  consumedVoucherIds: Set<string>;
  redeemedOfferIds: Set<string>;
}

/** `m` — `getVoucherPathwayCartContext`. */
export const getVoucherPathwayCartContext = ({
  offers,
  vouchers,
  redeemedOffers,
  orderDiscountDetails,
}: {
  offers: Offer[];
  vouchers: Voucher[] | null | undefined;
  redeemedOffers: Record<string, number> | null | undefined;
  orderDiscountDetails: OrderDiscountDetails | null | undefined;
}): VoucherPathwayCartContext => {
  const consumedVoucherCodes = new Set<string>();
  const consumedVoucherIds = new Set<string>();
  const redeemedOfferIds = new Set<string>();
  const codeById = new Map<string, string>();
  const active = normalizeCode(orderDiscountDetails?.voucher_reference_number);

  if (vouchers != null) {
    for (const voucher of vouchers) {
      const code = normalizeCode(voucher.code);
      if (code != null) codeById.set(voucher._id, code);
    }
  }

  for (const offer of offers) {
    if (offer.pathway_to_unlock_type !== PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER)
      continue;
    if (countAt(redeemedOffers, offer._id) <= 0) continue;
    const pathway = offer.pathway_to_unlock_type_vouchers;
    if (pathway == null || pathway.voucher_ids.length === 0) continue;
    redeemedOfferIds.add(offer._id);

    const codes = new Set<string>();
    for (const voucherId of pathway.voucher_ids) {
      consumedVoucherIds.add(voucherId);
      const code = codeById.get(voucherId);
      if (code != null) codes.add(code);
    }
    if (active != null && codes.has(active)) {
      consumedVoucherCodes.add(active);
      continue;
    }
    for (const code of codes) consumedVoucherCodes.add(code);
  }

  const soleConsumedCode =
    consumedVoucherCodes.size === 1
      ? (consumedVoucherCodes.values().next().value ?? null)
      : null;

  return {
    activeVoucherCode: active != null ? active : soleConsumedCode,
    consumedVoucherCodes,
    consumedVoucherIds,
    redeemedOfferIds,
  };
};

/** `_` — `isVoucherPathwayOfferBlockedByCartContext`. */
export const isVoucherPathwayOfferBlockedByCartContext = ({
  offer,
  cartContext,
}: {
  offer: Offer;
  cartContext: VoucherPathwayCartContext;
}): boolean =>
  offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER &&
  cartContext.redeemedOfferIds.size !== 0 &&
  !cartContext.redeemedOfferIds.has(offer._id);

/** `g` — `isExhaustedVoucherOffer`. */
export const isExhaustedVoucherOffer = ({
  offer,
  customerOfferRedemptionCounts,
  redeemedOffers,
  isRedeemed,
}: {
  offer: Offer | null;
  customerOfferRedemptionCounts: CustomerRedemptionCounts;
  redeemedOffers: Record<string, number> | null | undefined;
  isRedeemed: boolean;
}): boolean => {
  if (
    offer == null ||
    offer.pathway_to_unlock_type !== PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER ||
    offer.pathway_to_unlock_type_vouchers == null ||
    customerOfferRedemptionCounts == null ||
    !(isRedeemed || (redeemedOffers != null && Number(redeemedOffers[offer._id]) > 0))
  ) {
    return false;
  }
  const max = offer.pathway_to_unlock_type_vouchers.max_uses_per_customer;
  return (
    max != null && usedCount(customerOfferRedemptionCounts, offer._id) >= max
  );
};

/** `y` — `findLinkedVoucherOffers`. */
export const findLinkedVoucherOffers = ({
  voucherCode,
  offers,
  vouchers,
}: {
  voucherCode: string;
  offers: Offer[];
  vouchers: Voucher[];
}): { matchedVoucher: Voucher | null; linkedOffers: Offer[] } => {
  const code = voucherCode.trim().toUpperCase();
  if (!code) return { matchedVoucher: null, linkedOffers: [] };
  const matchedVoucher =
    vouchers.find((voucher) => voucher.code.trim().toUpperCase() === code) ??
    null;
  if (!matchedVoucher) return { matchedVoucher: null, linkedOffers: [] };
  const linkedOffers = offers.filter(
    (offer) =>
      offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER &&
      offer.pathway_to_unlock_type_vouchers?.voucher_ids.includes(
        matchedVoucher._id,
      ),
  );
  return { matchedVoucher, linkedOffers };
};

/** `b` — `buildClearVoucherDiscountDetails`. */
export const buildClearVoucherDiscountDetails = (
  details: OrderDiscountDetails | null | undefined,
): OrderDiscountDetails => ({
  ...(details ?? { total_discount: 0 }),
  voucher_discount_id: undefined,
  voucher_discount_name: undefined,
  voucher_discount: undefined,
  is_voucher_discount_percentage: undefined,
  voucher_discount_reason: undefined,
  voucher_reference_number: undefined,
  total_discount: 0,
});

/* -------------------------------------------------------------------------- */
/* spendable balances (`E`, `v`) and the gift-card redemption list             */
/* -------------------------------------------------------------------------- */

/** `getWalletGiftCardRedemptionClaims` — the claims currently in the cart. */
export const getWalletGiftCardRedemptionClaims = (
  redemptions: WalletGiftCardRedemption[] | null | undefined,
): WalletGiftCardRedemption[] => {
  if (!Array.isArray(redemptions)) return [];
  return redemptions.filter(
    (entry) =>
      entry != null &&
      toKey(entry.gift_card_id) != null &&
      positiveInt(entry.redeemed_times, 0) > 0,
  );
};

/**
 * `E` — `calculateCustomerSpendableVoucherCodes`.
 *
 * The customer's voucher counts minus whatever the cart has already claimed
 * against a voucher-pathway offer.
 */
export const calculateCustomerSpendableVoucherCodes = ({
  customerVoucherCodes,
  orderDiscountDetails,
  redeemedOffers,
  offers,
  vouchers,
}: {
  customerVoucherCodes: Record<string, number> | null | undefined;
  orderDiscountDetails: OrderDiscountDetails | null | undefined;
  redeemedOffers: Record<string, number> | null | undefined;
  offers: Offer[];
  vouchers: Voucher[] | null | undefined;
}): {
  voucherCodes: Record<string, number>;
  totalVouchers: number;
  activeVoucherCode: string | null;
  consumedVoucherClaims: number;
} => {
  const codes: Record<string, number> = {};
  if (customerVoucherCodes != null) {
    for (const [rawCode, rawCount] of Object.entries(customerVoucherCodes)) {
      const code = normalizeCode(rawCode);
      if (code == null) continue;
      const count = Number(rawCount);
      if (!Number.isFinite(count) || count <= 0) continue;
      codes[code] = Math.floor(count);
    }
  }

  const active = normalizeCode(orderDiscountDetails?.voucher_reference_number);
  const total = () =>
    Object.values(codes).reduce((sum, value) => sum + value, 0);

  if (vouchers == null || offers.length === 0 || redeemedOffers == null) {
    return {
      voucherCodes: codes,
      totalVouchers: total(),
      activeVoucherCode: active,
      consumedVoucherClaims: 0,
    };
  }

  const codeById = new Map<string, string>();
  for (const voucher of vouchers) {
    const code = normalizeCode(voucher.code);
    if (code != null) codeById.set(voucher._id, code);
  }

  const consumed: Record<string, number> = {};
  for (const offer of offers) {
    if (offer.pathway_to_unlock_type !== PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER)
      continue;
    const pathway = offer.pathway_to_unlock_type_vouchers;
    if (pathway == null || pathway.voucher_ids.length === 0) continue;
    const claims = countAt(redeemedOffers, offer._id);
    if (claims <= 0) continue;

    const linkedCodes = new Set<string>();
    for (const voucherId of pathway.voucher_ids) {
      const code = codeById.get(voucherId);
      if (code != null) linkedCodes.add(code);
    }
    if (linkedCodes.size === 0) continue;

    const [first] = linkedCodes;
    const target =
      linkedCodes.size === 1
        ? first
        : active != null && linkedCodes.has(active)
          ? active
          : null;
    if (target != null) consumed[target] = (consumed[target] ?? 0) + claims;
  }

  for (const code of Object.keys(consumed)) {
    if (codes[code] != null) {
      codes[code] = Math.max(0, codes[code] - consumed[code]);
    }
  }

  return {
    voucherCodes: codes,
    totalVouchers: total(),
    activeVoucherCode: active,
    consumedVoucherClaims: active != null ? (consumed[active] ?? 0) : 0,
  };
};

/**
 * The id the platform reserves for a wallet-cashback discount, so a cashback
 * redemption is not mistaken for a gift-card one.
 */
export const WALLET_CASHBACK_LOYALTY_DISCOUNT_ID = "WALLET_CASHBACK";

/** `v` — `calculateCustomerSpendableGiftCardCodes`. */
export const calculateCustomerSpendableGiftCardCodes = ({
  customerGiftCardCodes,
  loyaltyReferenceNumber,
  loyaltyDiscountId,
  walletGiftCardRedemptions,
  giftCards,
}: {
  customerGiftCardCodes: Record<string, number> | null | undefined;
  loyaltyReferenceNumber?: string | null;
  loyaltyDiscountId?: string | null;
  walletGiftCardRedemptions: WalletGiftCardRedemption[] | null | undefined;
  giftCards: GiftCard[] | null | undefined;
}): { giftCardCodes: Record<string, number>; totalGiftCards: number } => {
  const codes: Record<string, number> = {};
  const hasCatalogue = giftCards != null && giftCards.length > 0;
  const spendable = new Set<string>();
  const codeById = new Map<string, string>();

  if (hasCatalogue) {
    const now = Date.now();
    for (const card of giftCards) {
      const code = normalizeCode(card.code);
      if (code == null || !card.active) continue;
      if (card.expire_at != null) {
        const expiry = new Date(card.expire_at).getTime();
        if (Number.isFinite(expiry) && expiry < now) continue;
      }
      spendable.add(code);
      codeById.set(card._id, code);
    }
  }

  if (customerGiftCardCodes != null) {
    for (const [rawCode, rawCount] of Object.entries(customerGiftCardCodes)) {
      const code = normalizeCode(rawCode);
      if (code == null || (hasCatalogue && !spendable.has(code))) continue;
      const count = Number(rawCount);
      if (!Number.isFinite(count) || count <= 0) continue;
      codes[code] = Math.floor(count);
    }
  }

  const consumed: Record<string, number> = {};
  const claims = getWalletGiftCardRedemptionClaims(walletGiftCardRedemptions);
  for (const claim of claims) {
    const code = codeById.get(claim.gift_card_id);
    if (code != null && code.length !== 0) {
      consumed[code] = (consumed[code] ?? 0) + claim.redeemed_times;
    }
  }

  const consumedCodes = Object.keys(consumed);
  if (consumedCodes.length > 0) {
    for (const code of consumedCodes) {
      if (codes[code] != null) {
        codes[code] = Math.max(0, codes[code] - consumed[code]);
      }
    }
  } else {
    // No structured claim: fall back to the single legacy applied gift card,
    // which is identified by the loyalty reference number.
    const reference = normalizeCode(loyaltyReferenceNumber);
    const applied =
      loyaltyDiscountId != null &&
      loyaltyDiscountId !== WALLET_CASHBACK_LOYALTY_DISCOUNT_ID &&
      reference != null &&
      reference.length > 0
        ? reference
        : null;
    if (applied != null && codes[applied] != null) {
      codes[applied] = Math.max(0, codes[applied] - 1);
    }
  }

  return {
    giftCardCodes: codes,
    totalGiftCards: Object.values(codes).reduce((sum, value) => {
      const parsed = Number(value);
      return sum + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
    }, 0),
  };
};

/* -------------------------------------------------------------------------- */
/* loyalty points (`d`, `S`)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `d` — the points a POINTS-reward offer would award for the current basket.
 *
 * Only reachable with `includePendingRewardPoints`, which the wallet tile
 * passes as `false`, so the Points tile shows the SETTLED balance minus what
 * the cart has already spent — pending reward points are deliberately excluded.
 */
const pendingRewardPoints = (
  offer: Offer,
  netItemsValue: number,
  claims: number,
): number => {
  if (
    offer.reward_type !== OFFER_REWARD_TYPE_ENUM.POINTS ||
    offer.reward_points == null
  ) {
    return 0;
  }
  let multiplier = 1;
  if (!offer.reward_points.once_per_order) {
    const ratio = (value: number, per: number) =>
      Math.max(1, Math.floor(value / Math.max(1, per || 1)));
    if (offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.ORDERS) {
      const pathway = offer.pathway_to_unlock_type_orders;
      if (pathway != null) {
        const { total_amount, max_amount, max_claims } = pathway;
        if (
          netItemsValue >= total_amount &&
          (max_amount == null || netItemsValue <= max_amount)
        ) {
          multiplier = Math.min(
            ratio(netItemsValue, total_amount),
            max_claims == null ? Infinity : Math.max(1, max_claims),
          );
        }
      }
    } else if (
      offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER &&
      offer.pathway_to_unlock_type_vouchers != null
    ) {
      const { total_amount, max_amount, max_claims } =
        offer.pathway_to_unlock_type_vouchers;
      if (
        total_amount != null &&
        netItemsValue >= total_amount &&
        (max_amount == null || netItemsValue <= max_amount)
      ) {
        multiplier = Math.min(
          ratio(netItemsValue, total_amount),
          max_claims == null ? Infinity : Math.max(1, max_claims),
        );
      }
    }
  }
  if (Number.isFinite(claims) && claims > 0) {
    multiplier = Math.min(multiplier, Math.floor(claims));
  }
  return (offer.reward_points.num_of_points ?? 0) * multiplier;
};

/**
 * `S` — `calculateCustomerLoyaltyPoints`.
 *
 * The balance the Points tile renders: the stored balance, minus the points a
 * POINTS-PATHWAY offer already claimed in this cart will spend, plus — only
 * when `includePendingRewardPoints` is set, which the tile does NOT — the
 * points a POINTS-REWARD offer would earn.
 */
export const calculateCustomerLoyaltyPoints = ({
  customerLoyaltyPoints,
  orderDiscountDetails,
  redeemedOffers,
  currentItemsPrice,
  totalDiscountApplied,
  offers,
  includePendingRewardPoints = false,
}: {
  customerLoyaltyPoints?: number | null;
  orderDiscountDetails?: OrderDiscountDetails | null;
  redeemedOffers?: Record<string, number> | null;
  currentItemsPrice?: number | null;
  totalDiscountApplied?: number | null;
  offers: Offer[];
  includePendingRewardPoints?: boolean;
}): {
  loyaltyPoints: number;
  addedLoyaltyPoints: number;
  deductedLoyaltyPoints: number;
} => {
  const netItemsValue = (currentItemsPrice ?? 0) - (totalDiscountApplied ?? 0);
  const balance = customerLoyaltyPoints ?? 0;
  let loyaltyPoints = balance;
  let addedLoyaltyPoints = balance;
  let deductedLoyaltyPoints = balance;

  const relevant: Record<string, Offer> = {};
  const perClaimItems: Record<string, number> = {};
  for (const offer of offers) {
    const isPointsReward = offer.reward_type === OFFER_REWARD_TYPE_ENUM.POINTS;
    const isPointsPathway =
      offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.POINTS;
    if (!isPointsReward && !isPointsPathway) continue;
    relevant[offer._id] = offer;
    if (offer.reward_type === OFFER_REWARD_TYPE_ENUM.ITEMS) {
      perClaimItems[offer._id] = Math.max(1, offer.reward_items?.num_of_items ?? 1);
    } else if (offer.reward_type === OFFER_REWARD_TYPE_ENUM.DISCOUNT_ITEMS) {
      perClaimItems[offer._id] = Math.max(
        1,
        offer.reward_discount_items?.num_of_items ?? 1,
      );
    } else {
      perClaimItems[offer._id] = 1;
    }
  }

  if (Object.keys(relevant).length > 0) {
    const claims: Record<string, number> = {};
    const record = (offerId: string | undefined | null, value: unknown) => {
      if (offerId == null || relevant[offerId] == null) return;
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      claims[offerId] = Math.max(claims[offerId] ?? 0, Math.max(1, Math.floor(parsed)));
    };

    if (redeemedOffers != null) {
      for (const offerId of Object.keys(redeemedOffers)) {
        record(offerId, redeemedOffers[offerId]);
      }
    }
    if (orderDiscountDetails != null) {
      record(orderDiscountDetails.loyalty_discount_id, 1);
      record(orderDiscountDetails.voucher_discount_id, 1);
    }

    for (const offerId of Object.keys(claims)) {
      const offer = relevant[offerId];
      const count = claims[offerId] ?? 0;
      if (offer == null || count <= 0) continue;
      if (
        offer.reward_type === OFFER_REWARD_TYPE_ENUM.POINTS &&
        includePendingRewardPoints
      ) {
        const earned = pendingRewardPoints(offer, netItemsValue, count);
        loyaltyPoints += earned;
        addedLoyaltyPoints += earned;
      }
      if (offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.POINTS) {
        const spent =
          (offer.pathway_to_unlock_type_points?.num_of_points ?? 0) * count;
        loyaltyPoints -= spent;
        deductedLoyaltyPoints += spent;
      }
    }
  }

  return { loyaltyPoints, addedLoyaltyPoints, deductedLoyaltyPoints };
};

/* -------------------------------------------------------------------------- */
/* presentation helpers (`eu`, `ec`, `ed`, `sc`, `Y`, `F`)                     */
/* -------------------------------------------------------------------------- */

/** `eu` — `buildRewardPreview`. */
export const buildRewardPreview = (offer: Offer): OfferRewardPreview | null => {
  switch (offer.reward_type) {
    case OFFER_REWARD_TYPE_ENUM.DISCOUNT: {
      if (offer.reward_discount == null) return null;
      const {
        discount_value,
        is_discount_percentage,
        max_discount_amount,
        max_discount_percentage,
      } = offer.reward_discount;
      let label = is_discount_percentage
        ? `${discount_value}% off`
        : `${discount_value} off`;
      if (is_discount_percentage && max_discount_amount != null) {
        label += ` (max ${max_discount_amount})`;
      }
      if (!is_discount_percentage && max_discount_percentage != null) {
        label += ` (max ${max_discount_percentage}%)`;
      }
      return {
        type: OFFER_REWARD_TYPE_ENUM.DISCOUNT,
        label,
        discount_value,
        is_percentage: is_discount_percentage,
        max_discount_amount,
        max_discount_percentage,
      };
    }
    case OFFER_REWARD_TYPE_ENUM.DISCOUNT_ITEMS: {
      if (offer.reward_discount_items == null) return null;
      const { discount_value, is_discount_percentage, num_of_items } =
        offer.reward_discount_items;
      return {
        type: OFFER_REWARD_TYPE_ENUM.DISCOUNT_ITEMS,
        label: is_discount_percentage
          ? `${discount_value}% off on ${num_of_items} item(s)`
          : `${discount_value} off on ${num_of_items} item(s)`,
        discount_value,
        is_percentage: is_discount_percentage,
        free_items_count: num_of_items,
      };
    }
    case OFFER_REWARD_TYPE_ENUM.ITEMS:
      if (offer.reward_items == null) return null;
      return {
        type: OFFER_REWARD_TYPE_ENUM.ITEMS,
        label: `Free ${offer.reward_items.num_of_items} item(s)`,
        free_items_count: offer.reward_items.num_of_items,
      };
    case OFFER_REWARD_TYPE_ENUM.POINTS:
      if (offer.reward_points == null) return null;
      return {
        type: OFFER_REWARD_TYPE_ENUM.POINTS,
        label: `${offer.reward_points.num_of_points} points`,
        points_value: offer.reward_points.num_of_points,
      };
    case OFFER_REWARD_TYPE_ENUM.CASHBACK: {
      if (offer.reward_cashback == null) return null;
      const { cashback_value, is_cashback_percentage } = offer.reward_cashback;
      return {
        type: OFFER_REWARD_TYPE_ENUM.CASHBACK,
        label: is_cashback_percentage
          ? `${cashback_value}% cashback`
          : `${cashback_value} cashback`,
        cashback_value,
        is_percentage: is_cashback_percentage,
      };
    }
    case OFFER_REWARD_TYPE_ENUM.GIFT_CARDS:
      if (offer.reward_gift_cards == null) return null;
      return {
        type: OFFER_REWARD_TYPE_ENUM.GIFT_CARDS,
        label: "Gift card reward",
      };
    case OFFER_REWARD_TYPE_ENUM.VOUCHER:
      if (offer.reward_voucher == null) return null;
      return {
        type: OFFER_REWARD_TYPE_ENUM.VOUCHER,
        label: "Voucher code reward",
      };
    default:
      return null;
  }
};

/** `ec` — the item ids a reward can be spent on. */
export const rewardEligibleItemIds = (offer: Offer): string[] => {
  if (offer.reward_type === OFFER_REWARD_TYPE_ENUM.ITEMS && offer.reward_items) {
    return offer.reward_items.item_ids ?? [];
  }
  if (
    offer.reward_type === OFFER_REWARD_TYPE_ENUM.DISCOUNT_ITEMS &&
    offer.reward_discount_items
  ) {
    return offer.reward_discount_items.item_ids ?? [];
  }
  return [];
};

/** `ed` — the category ids an ITEMS pathway counts against. */
export const pathwayEligibleCategoryIds = (offer: Offer): string[] =>
  offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.ITEMS &&
  offer.pathway_to_unlock_type_items != null
    ? (offer.pathway_to_unlock_type_items.category_ids ?? [])
    : [];

/**
 * `sc` — the drawer's own discount preview string, distinct from `eu`'s label:
 * `20% off (up to Rs 500, max 30% of order)`.
 */
export const buildDiscountPreviewText = (
  discount:
    | {
        discount_value: number;
        is_discount_percentage?: boolean;
        max_discount_amount?: number | null;
        max_discount_percentage?: number | null;
      }
    | null
    | undefined,
  currencySymbol: string | undefined,
): string | null => {
  if (!discount) return null;
  const {
    discount_value,
    is_discount_percentage,
    max_discount_amount,
    max_discount_percentage,
  } = discount;
  const base = is_discount_percentage
    ? `${parseNum(discount_value)}% off`
    : `${localeAmount(currencySymbol, discount_value)} off`;
  const suffixes: string[] = [];
  if (max_discount_amount != null && max_discount_amount > 0) {
    suffixes.push(`up to ${localeAmount(currencySymbol, max_discount_amount)}`);
  }
  if (max_discount_percentage != null && max_discount_percentage > 0) {
    suffixes.push(`max ${parseNum(max_discount_percentage)}% of order`);
  }
  return suffixes.length === 0 ? base : `${base} (${suffixes.join(", ")})`;
};

/** The gift-card row's green reward preview. */
export const getGiftCardRewardPreviewText = ({
  giftCard,
  currencySymbol,
}: {
  giftCard: GiftCard;
  currencySymbol?: string;
}): string | null => {
  if (giftCard.reward_discount != null) {
    return buildDiscountPreviewText(giftCard.reward_discount, currencySymbol);
  }
  if (giftCard.reward_items != null) {
    return `Free ${giftCard.reward_items.num_of_items} item(s)`;
  }
  if (giftCard.reward_points != null) {
    return `${giftCard.reward_points.num_of_points} points`;
  }
  if (giftCard.reward_cashback != null) {
    const { cashback_value, is_cashback_percentage } = giftCard.reward_cashback;
    return is_cashback_percentage
      ? `${parseNum(cashback_value)}% cashback`
      : `${localeAmount(currencySymbol, cashback_value)} cashback`;
  }
  return null;
};

/**
 * The emoji stripper the card runs `stats.message` through before display —
 * the engine authors its messages with ✅/⛔/🎉 and the card shows them plain.
 */
export const stripMessageEmoji = (message: string | null | undefined): string =>
  (message ?? "")
    .replace(
      /[\u{1F600}-\u{1F9FF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F000}-\u{1FAFF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}\u{2705}\u{26D4}\u{1F389}\u{1F513}]/gu,
      "",
    )
    .replace(/\s{2,}/g, " ")
    .trim();

/** `Y` — `buildRedeemedOffersClaimPatch`. */
export const buildRedeemedOffersClaimPatch = ({
  currentRedeemedOffers,
  offerId,
  claimsIncrement = 1,
}: {
  currentRedeemedOffers: Record<string, number> | null | undefined;
  offerId: string;
  claimsIncrement?: number;
}): { redeemed_offers?: Record<string, number> | null } => {
  const key = toKey(offerId);
  if (key == null) return {};
  const next: Record<string, number> = {};
  for (const [rawId, rawCount] of Object.entries(currentRedeemedOffers ?? {})) {
    const id = toKey(rawId);
    if (id == null) continue;
    const count = positiveInt(rawCount, 0);
    if (count <= 0) continue;
    next[id] = count;
  }
  const increment = positiveInt(claimsIncrement, 1);
  next[key] = positiveInt(next[key], 0) + increment;
  return { redeemed_offers: Object.keys(next).length > 0 ? next : null };
};

/** `F` — `getAutoApplyOfferCandidates`. */
export const getAutoApplyOfferCandidates = ({
  offersWithStats,
  suppressedOfferIds,
}: {
  offersWithStats: OfferWithStats[] | null | undefined;
  suppressedOfferIds?: Record<string, boolean> | null;
}): { offer: Offer; claimsIncrement?: number }[] => {
  if (offersWithStats == null || offersWithStats.length === 0) return [];
  const candidates: { offer: Offer; claimsIncrement?: number }[] = [];
  for (const { offer, stats } of offersWithStats) {
    const id = toKey(offer._id);
    if (id == null) continue;
    const repeatablePoints =
      offer.reward_type === OFFER_REWARD_TYPE_ENUM.POINTS &&
      offer.reward_points != null &&
      !offer.reward_points.once_per_order &&
      stats.redeemed_times > 0;
    const autoApply = offer.auto_apply || repeatablePoints;
    if (!stats.claimable || stats.invalid_offer || !autoApply) continue;
    if (suppressedOfferIds?.[id]) continue;
    const claimsIncrement =
      offer.reward_type === OFFER_REWARD_TYPE_ENUM.POINTS
        ? getOfferClaimIncrementFromStats({ stats })
        : undefined;
    if (claimsIncrement != null && claimsIncrement <= 0) continue;
    candidates.push({
      offer,
      ...(claimsIncrement != null ? { claimsIncrement } : {}),
    });
  }
  return candidates;
};

/** `U` — a stable hash of an id list, used to avoid redundant re-renders. */
export const getOffersIdsHashKey = (ids: (string | null)[] | null | undefined) =>
  (ids ?? [])
    .map((id) => toKey(id))
    .sort()
    .join("|");

/** The three id lists the redemption-count endpoint is asked for (`d`). */
export const collectRedemptionReferenceIds = (
  offers: Offer[],
): { offer_ids: string[]; voucher_ids: string[]; gift_card_ids: string[] } => {
  const offerIds: string[] = [];
  const voucherIds = new Set<string>();
  const giftCardIds = new Set<string>();
  for (const offer of offers) {
    offerIds.push(offer._id);
    if (
      offer.pathway_to_unlock_type === PATHWAY_TO_UNLOCK_TYPE_ENUM.VOUCHER &&
      offer.pathway_to_unlock_type_vouchers != null
    ) {
      for (const id of offer.pathway_to_unlock_type_vouchers.voucher_ids) {
        voucherIds.add(id);
      }
    }
    if (
      offer.reward_type === OFFER_REWARD_TYPE_ENUM.VOUCHER &&
      offer.reward_voucher != null
    ) {
      for (const id of Object.keys(offer.reward_voucher.voucher_ids)) {
        voucherIds.add(id);
      }
    }
    if (
      offer.reward_type === OFFER_REWARD_TYPE_ENUM.GIFT_CARDS &&
      offer.reward_gift_cards != null
    ) {
      for (const id of Object.keys(offer.reward_gift_cards.gift_card_ids)) {
        giftCardIds.add(id);
      }
    }
  }
  return {
    offer_ids: offerIds,
    voucher_ids: Array.from(voucherIds),
    gift_card_ids: Array.from(giftCardIds),
  };
};

/** `p` — normalise the redemption-count reply into the two-bucket shape. */
export const normalizeRedemptionCounts = (
  payload: unknown,
): CustomerRedemptionCounts => {
  if (payload == null) return null;
  const raw = payload as Record<string, Record<string, number> | undefined>;
  return {
    [CUSTOMER_REDEMPTION_TYPE_ENUM.ASSIGNED]:
      raw[CUSTOMER_REDEMPTION_TYPE_ENUM.ASSIGNED] ?? {},
    [CUSTOMER_REDEMPTION_TYPE_ENUM.USED]:
      raw[CUSTOMER_REDEMPTION_TYPE_ENUM.USED] ?? {},
  };
};

export type { WalletRedemptions };
